import { useState, useEffect } from "react";
import { X, Target, Save, Ban, Trash2, Plus, ShieldAlert, Zap } from "lucide-react";
import { deployerStore, type DeployerHistory } from "@/lib/deployerStore";

interface DeployerRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialAddress?: string;
}

export function DeployerRulesModal({ isOpen, onClose, initialAddress = "" }: DeployerRulesModalProps) {
  const [activeTab, setActiveTab] = useState<"snipe" | "blacklist">("snipe");
  const [address, setAddress] = useState(initialAddress);
  const [customName, setCustomName] = useState("");
  const [customColor, setCustomColor] = useState("text-blue-400");
  const [snipeAmount, setSnipeAmount] = useState<string>("0.5");
  const [takeProfitPct, setTakeProfitPct] = useState<string>("50");
  const [stopLossPct, setStopLossPct] = useState<string>("20");
  const [exitOnDevSell, setExitOnDevSell] = useState<boolean>(true);
  
  const [rules, setRules] = useState<DeployerHistory[]>([]);

  useEffect(() => {
    if (isOpen) {
      setRules(deployerStore.getAllRules());
      if (initialAddress) {
        setAddress(initialAddress);
        const existing = deployerStore.getHistory(initialAddress);
        if (existing) {
          if (existing.customName) setCustomName(existing.customName);
          if (existing.customColor) setCustomColor(existing.customColor);
          if (existing.snipeAmountSol) setSnipeAmount(String(existing.snipeAmountSol));
          if (existing.takeProfitPct != null) setTakeProfitPct(String(existing.takeProfitPct));
          if (existing.stopLossPct != null) setStopLossPct(String(existing.stopLossPct));
          if (existing.exitOnDevSell != null) setExitOnDevSell(existing.exitOnDevSell);
          if (existing.isBlacklisted) setActiveTab("blacklist");
          else if (existing.isAutoSnipe) setActiveTab("snipe");
        }
      }
    }
  }, [isOpen, initialAddress]);

  if (!isOpen) return null;

  const handleSaveSnipe = () => {
    if (!address) return;
    deployerStore.setRule(address, {
      customName,
      customColor,
      isAutoSnipe: true,
      snipeAmountSol: parseFloat(snipeAmount) || 0,
      takeProfitPct: parseFloat(takeProfitPct) || 50,
      stopLossPct: parseFloat(stopLossPct) || 20,
      exitOnDevSell,
      isBlacklisted: false
    });
    setRules(deployerStore.getAllRules());
    setAddress("");
    setCustomName("");
  };

  const handleBlacklist = () => {
    if (!address) return;
    deployerStore.setRule(address, {
      isBlacklisted: true,
      isAutoSnipe: false,
      customName: "",
      customColor: ""
    });
    setRules(deployerStore.getAllRules());
    setAddress("");
  };

  const handleRemove = (addr: string) => {
    deployerStore.setRule(addr, {
      isBlacklisted: false,
      isAutoSnipe: false,
      customName: "",
      customColor: ""
    });
    setRules(deployerStore.getAllRules());
  };

  const snipeRules = rules.filter(r => r.isAutoSnipe || r.customName);
  const blacklistRules = rules.filter(r => r.isBlacklisted);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-zinc-950 border border-white/10 rounded-xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-black/40">
          <h2 className="text-lg font-black text-white flex items-center gap-2">
            <Target className="size-5 text-amber-500" />
            Dev Sniper & Rules
          </h2>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white transition-colors rounded hover:bg-white/5">
            <X className="size-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 bg-zinc-900/50">
          <button 
            className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === "snipe" ? "text-amber-400 border-b-2 border-amber-400 bg-amber-500/5" : "text-white/40 hover:text-white/80"}`}
            onClick={() => setActiveTab("snipe")}
          >
            Dev Snipe
          </button>
          <button 
            className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${activeTab === "blacklist" ? "text-rose-400 border-b-2 border-rose-400 bg-rose-500/5" : "text-white/40 hover:text-white/80"}`}
            onClick={() => setActiveTab("blacklist")}
          >
            Blacklist
          </button>
        </div>

        {/* Content */}
        <div className="p-4 flex-1 overflow-y-auto">
          {activeTab === "snipe" && (
            <div className="space-y-6">
              <div className="space-y-4 p-4 bg-black/40 border border-white/5 rounded-lg">
                <h3 className="text-xs font-black text-white/60 uppercase tracking-widest flex items-center gap-2">
                  <Plus className="size-3" /> Add Dev
                </h3>
                
                <div>
                  <label className="block text-[10px] text-white/40 uppercase font-bold mb-1">Wallet Address</label>
                  <input 
                    type="text" 
                    placeholder="Enter dev wallet..."
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full bg-zinc-900 border border-white/10 rounded px-3 py-2 text-sm text-white font-mono placeholder:text-white/20 focus:outline-none focus:border-amber-500/50"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] text-white/40 uppercase font-bold mb-1">Custom Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Good Dev"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      className="w-full bg-zinc-900 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-amber-500/50"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/40 uppercase font-bold mb-1">Snipe Amount (SOL)</label>
                    <input 
                      type="number"
                      step="0.1"
                      min="0"
                      value={snipeAmount}
                      onChange={(e) => setSnipeAmount(e.target.value)}
                      className="w-full bg-zinc-900 border border-white/10 rounded px-3 py-2 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-amber-500/50"
                    />
                  </div>
                </div>

                {/* Quick-Flip & Dump Shield Strategy */}
                <div className="p-3 bg-zinc-900/70 border border-amber-500/20 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase text-amber-400 tracking-wider flex items-center gap-1.5">
                      <Zap className="size-3 text-amber-400" />
                      Quick-Flip & Dump Shield
                    </span>
                    <span className="text-[8px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 font-bold">
                      HIGH WIN RATE
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[9px] text-white/50 uppercase font-bold">Take Profit %</label>
                        <div className="flex gap-1">
                          {[30, 50, 100].map(pct => (
                            <button
                              key={pct}
                              type="button"
                              onClick={() => setTakeProfitPct(String(pct))}
                              className={`text-[8px] font-bold px-1 rounded border ${takeProfitPct === String(pct) ? 'bg-emerald-500/30 text-emerald-300 border-emerald-500/50' : 'bg-black/30 text-white/40 border-white/5'}`}
                            >
                              +{pct}%
                            </button>
                          ))}
                        </div>
                      </div>
                      <input 
                        type="number"
                        min="5"
                        max="1000"
                        value={takeProfitPct}
                        onChange={(e) => setTakeProfitPct(e.target.value)}
                        className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-xs text-emerald-300 font-mono focus:outline-none focus:border-emerald-500/50"
                        placeholder="50"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[9px] text-white/50 uppercase font-bold">Stop Loss %</label>
                        <div className="flex gap-1">
                          {[15, 20, 30].map(pct => (
                            <button
                              key={pct}
                              type="button"
                              onClick={() => setStopLossPct(String(pct))}
                              className={`text-[8px] font-bold px-1 rounded border ${stopLossPct === String(pct) ? 'bg-rose-500/30 text-rose-300 border-rose-500/50' : 'bg-black/30 text-white/40 border-white/5'}`}
                            >
                              -{pct}%
                            </button>
                          ))}
                        </div>
                      </div>
                      <input 
                        type="number"
                        min="5"
                        max="90"
                        value={stopLossPct}
                        onChange={(e) => setStopLossPct(e.target.value)}
                        className="w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-xs text-rose-300 font-mono focus:outline-none focus:border-rose-500/50"
                        placeholder="20"
                      />
                    </div>
                  </div>

                  {/* Dump Shield Toggle */}
                  <div 
                    onClick={() => setExitOnDevSell(!exitOnDevSell)}
                    className={`flex items-start gap-2.5 p-2 rounded border cursor-pointer transition-all ${
                      exitOnDevSell 
                        ? 'bg-rose-500/10 border-rose-500/30 text-white' 
                        : 'bg-black/20 border-white/5 text-white/40'
                    }`}
                  >
                    <input 
                      type="checkbox"
                      checked={exitOnDevSell}
                      onChange={(e) => setExitOnDevSell(e.target.checked)}
                      className="mt-0.5 accent-rose-500"
                    />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-black uppercase tracking-wider text-rose-400 flex items-center gap-1">
                        <ShieldAlert className="size-3" />
                        Emergency Exit on Dev Sell (Dump Shield)
                      </span>
                      <span className="text-[8px] text-white/50 leading-tight">
                        Exits 100% position immediately the instant the dev's wallet sells, front-running the rug.
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] text-white/40 uppercase font-bold mb-2">Color Label</label>
                  <div className="flex gap-2">
                    {["text-blue-400", "text-emerald-400", "text-amber-400", "text-fuchsia-400", "text-cyan-400"].map(c => (
                      <button 
                        key={c}
                        onClick={() => setCustomColor(c)}
                        className={`size-6 rounded-full border-2 ${customColor === c ? 'border-white' : 'border-transparent'} ${c.replace('text-', 'bg-').replace('400', '500')} transition-all hover:scale-110`}
                      />
                    ))}
                  </div>
                </div>

                <button 
                  onClick={handleSaveSnipe}
                  disabled={!address || !customName}
                  className="w-full py-2 bg-amber-500 hover:bg-amber-400 text-black font-black uppercase text-sm rounded flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="size-4" /> Save Rule
                </button>
              </div>

              <div>
                <h3 className="text-xs font-black text-white/60 uppercase tracking-widest mb-3">Followed Devs</h3>
                {snipeRules.length === 0 ? (
                  <div className="text-center py-8 text-white/20 text-sm font-medium">
                    No devs followed yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {snipeRules.map(r => (
                      <div key={r.address} className="flex items-center justify-between p-3 bg-zinc-900/50 border border-white/5 rounded">
                        <div className="flex flex-col gap-0.5">
                          <span className={`text-sm font-bold ${r.customColor}`}>{r.customName}</span>
                          <span className="text-xs text-white/40 font-mono">{r.address.slice(0,4)}...{r.address.slice(-4)}</span>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className="text-[8px] font-mono text-emerald-400 bg-emerald-500/10 px-1 py-0.2 rounded border border-emerald-500/20">
                              TP: +{r.takeProfitPct ?? 50}%
                            </span>
                            <span className="text-[8px] font-mono text-rose-400 bg-rose-500/10 px-1 py-0.2 rounded border border-rose-500/20">
                              SL: -{r.stopLossPct ?? 20}%
                            </span>
                            {r.exitOnDevSell !== false && (
                              <span className="text-[8px] font-mono text-amber-400 bg-amber-500/10 px-1 py-0.2 rounded border border-amber-500/20 flex items-center gap-0.5">
                                <ShieldAlert className="size-2" /> Dump Shield
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {r.isAutoSnipe && (
                            <div className="text-xs font-bold text-amber-400 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/20">
                              Auto: {r.snipeAmountSol} SOL
                            </div>
                          )}
                          <button onClick={() => handleRemove(r.address)} className="text-white/20 hover:text-rose-400 transition-colors">
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "blacklist" && (
            <div className="space-y-6">
              <div className="space-y-4 p-4 bg-black/40 border border-white/5 rounded-lg">
                <h3 className="text-xs font-black text-rose-500 uppercase tracking-widest flex items-center gap-2">
                  <Ban className="size-3" /> Add to Blacklist
                </h3>
                
                <div>
                  <label className="block text-[10px] text-white/40 uppercase font-bold mb-1">Wallet Address</label>
                  <input 
                    type="text" 
                    placeholder="Enter serial rugger wallet..."
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full bg-zinc-900 border border-white/10 rounded px-3 py-2 text-sm text-white font-mono placeholder:text-white/20 focus:outline-none focus:border-rose-500/50"
                  />
                </div>

                <button 
                  onClick={handleBlacklist}
                  disabled={!address}
                  className="w-full py-2 bg-rose-500 hover:bg-rose-400 text-black font-black uppercase text-sm rounded flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Ban className="size-4" /> Blacklist Dev
                </button>
              </div>

              <div>
                <h3 className="text-xs font-black text-white/60 uppercase tracking-widest mb-3">Blacklisted Devs</h3>
                {blacklistRules.length === 0 ? (
                  <div className="text-center py-8 text-white/20 text-sm font-medium">
                    No blacklisted devs
                  </div>
                ) : (
                  <div className="space-y-2">
                    {blacklistRules.map(r => (
                      <div key={r.address} className="flex items-center justify-between p-3 bg-rose-950/20 border border-rose-500/20 rounded">
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-rose-500">Blacklisted</span>
                          <span className="text-xs text-white/40 font-mono">{r.address.slice(0,4)}...{r.address.slice(-4)}</span>
                        </div>
                        <button onClick={() => handleRemove(r.address)} className="text-sm font-bold text-white/40 hover:text-white transition-colors">
                          Unban
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
